// ホイールレイアウト計算 — スコープ確定書 §5.1.1, §5.1.3 に準拠

import type { Item } from './types';

export interface WheelSegment {
  /** 元 items 配列内のインデックス */
  itemIndex: number;
  text: string;
  /** レイアウト座標系 [0, 2π) — 12時方向を 0 として時計回り */
  startAngle: number;
  endAngle: number;
  midAngle: number;
  /** PALETTE インデックス (フォールバック用) */
  colorIndex: number;
  /** 解決済み描画色 (customColor 優先、無ければパレット) */
  color: string;
}

export interface WheelLayout {
  segments: WheelSegment[];
}

// Windows 版 RRoulette 「デフォルト」プリセットと同一 (design_settings.py:99-103)
export const PALETTE: readonly string[] = [
  '#E74C3C', '#3498DB', '#2ECC71', '#F39C12', '#9B59B6',
  '#1ABC9C', '#E67E22', '#27AE60', '#E91E63', '#00BCD4',
  '#8BC34A', '#FF5722', '#2980B9', '#8E44AD', '#F4D03F',
];

/** 各 enabled 項目の重み（％ベース）を計算する。
 *  - default: 残り％を非固定項目で均等配分
 *  - weight:  probValue（乗数）に応じて残り％を配分
 *  - fixed:   probValue%（絶対割合）
 *  splitCount はこの段階では考慮しない（合計面積は不変）。
 */
function computeItemWeights(items: Item[]): { idx: number; weight: number }[] {
  const enabled = items
    .map((item, idx) => ({ item, idx }))
    .filter(({ item }) => item.enabled);

  if (enabled.length === 0) return [];

  const fixedSum = enabled.reduce(
    (s, { item }) => (item.probMode === 'fixed' ? s + (item.probValue ?? 0) : s),
    0,
  );
  const remainingPct = Math.max(0, 100 - fixedSum);

  const nonFixed = enabled.filter(({ item }) => item.probMode !== 'fixed');
  const nonFixedBase = nonFixed.reduce(
    (s, { item }) => s + (item.probMode === 'weight' ? (item.probValue ?? 1) : 1),
    0,
  );

  return enabled.map(({ item, idx }) => {
    let pct: number;
    if (item.probMode === 'fixed') {
      pct = item.probValue ?? 0;
    } else {
      const base = item.probMode === 'weight' ? (item.probValue ?? 1) : 1;
      pct = nonFixedBase > 0 ? (base / nonFixedBase) * remainingPct : 0;
    }
    return { idx, weight: pct };
  });
}

/** スロット配置: 入力順 (itemIndex 昇順) を維持しつつ、
 *  各項目の splits をストライド total/N で離して配置する。
 *  - 1 番目の項目の最初の split が slot 0 (= 12時) に来るよう ideal 式は floor(k * stride)
 *  - 競合があれば最近傍の空きスロットへフォールバック
 */
type Slot = { itemIndex: number; splitIndex: number };

function distributeSlots(
  units: { itemIndex: number; N: number }[],
): Slot[] {
  const total = units.reduce((s, u) => s + u.N, 0);
  if (total === 0) return [];
  const slots: (Slot | null)[] = new Array(total).fill(null);

  // 入力順 (itemIndex 昇順) に処理 → 1 番目の項目が最初に slot 0 を取る
  const ordered = [...units].sort((a, b) => a.itemIndex - b.itemIndex);

  for (const u of ordered) {
    const N = u.N;
    if (N <= 0) continue;
    const stride = total / N;

    // ベスト startOff (競合最小) を探す
    let bestStartOff = 0;
    let bestConflicts = Infinity;
    for (let startOff = 0; startOff < total; startOff++) {
      let conflicts = 0;
      for (let k = 0; k < N; k++) {
        const pos = (startOff + Math.floor(k * stride)) % total;
        if (slots[pos] !== null) conflicts++;
      }
      if (conflicts < bestConflicts) {
        bestConflicts = conflicts;
        bestStartOff = startOff;
        if (conflicts === 0) break;
      }
    }

    // ideal 位置に配置、競合は最近傍の空きへ
    for (let k = 0; k < N; k++) {
      let pos = (bestStartOff + Math.floor(k * stride)) % total;
      if (slots[pos] !== null) {
        outer: for (let d = 1; d <= total; d++) {
          for (const sign of [1, -1] as const) {
            const tryPos = ((pos + sign * d) % total + total) % total;
            if (slots[tryPos] === null) {
              pos = tryPos;
              break outer;
            }
          }
        }
      }
      slots[pos] = {
        itemIndex: u.itemIndex,
        splitIndex: k,
      };
    }
  }

  return slots.filter((s): s is Slot => s !== null);
}

/** 項目インデックス + (任意) item からセグメント色を解決。
 *  - item.customColor があればそれを優先 (シャッフルしても色不変)
 *  - 無ければパレット既定色
 */
export function colorForItem(
  itemIndex: number,
  item?: { customColor?: string },
): string {
  if (item && item.customColor) return item.customColor;
  return PALETTE[itemIndex % PALETTE.length];
}

/** 各項目の確率 (%) を Map<元配列内インデックス, %> で返す。
 *  - 無効 (enabled=false) な項目はマップに含まれない
 *  - splitCount は確率に影響しない（合計面積は不変）
 */
export function computeItemPercentages(items: Item[]): Map<number, number> {
  const result = new Map<number, number>();
  const enabled = items
    .map((item, idx) => ({ item, idx }))
    .filter(({ item }) => item.enabled);
  if (enabled.length === 0) return result;

  const fixedSum = enabled.reduce(
    (s, { item }) => (item.probMode === 'fixed' ? s + (item.probValue ?? 0) : s),
    0,
  );
  const remainingPct = Math.max(0, 100 - fixedSum);
  const nonFixed = enabled.filter(({ item }) => item.probMode !== 'fixed');
  const nonFixedBase = nonFixed.reduce(
    (s, { item }) => s + (item.probMode === 'weight' ? (item.probValue ?? 1) : 1),
    0,
  );

  for (const { item, idx } of enabled) {
    let pct: number;
    if (item.probMode === 'fixed') {
      pct = item.probValue ?? 0;
    } else {
      const base = item.probMode === 'weight' ? (item.probValue ?? 1) : 1;
      pct = nonFixedBase > 0 ? (base / nonFixedBase) * remainingPct : 0;
    }
    result.set(idx, pct);
  }
  return result;
}

/** Items からホイールレイアウトを構築する。
 *  - 重み計算は splitCount を含めない（合計面積は不変）
 *  - 同一項目の splits は円周上に最大限分散して配置
 *  - 色 (colorIndex / color) は元配列内インデックスで決まる
 *  - customColor が設定されていればそれを優先
 *
 *  @param items 表示順の項目配列 (シャッフル済みでも良い)
 *  @param originalIndexMap 表示位置 i に対応する「元配列での位置」。省略時は i をそのまま使う。
 */
export function buildLayout(
  items: Item[],
  originalIndexMap?: number[],
): WheelLayout {
  const weights = computeItemWeights(items);
  const total = weights.reduce((s, w) => s + w.weight, 0);

  if (total === 0) return { segments: [] };

  const toOrig = (i: number) =>
    originalIndexMap && i < originalIndexMap.length ? originalIndexMap[i] : i;

  const distrUnits = weights.map(({ idx }) => ({
    itemIndex: idx,
    N: Math.max(1, items[idx].splitCount),
  }));

  const slots = distributeSlots(distrUnits);

  const weightByItemIdx = new Map<number, number>();
  weights.forEach(({ idx, weight }) => {
    const N = Math.max(1, items[idx].splitCount);
    weightByItemIdx.set(idx, weight / N);
  });

  let angle = 0;
  const segments: WheelSegment[] = [];
  for (const slot of slots) {
    const wPer = weightByItemIdx.get(slot.itemIndex) ?? 0;
    const sweep = (wPer / total) * Math.PI * 2;
    const start = angle;
    const end = start + sweep;
    const origIdx = toOrig(slot.itemIndex);
    const item = items[slot.itemIndex];
    const color = item.customColor || PALETTE[origIdx % PALETTE.length];
    segments.push({
      itemIndex: origIdx,
      text: item.text,
      startAngle: start,
      endAngle: end,
      midAngle: (start + end) / 2,
      colorIndex: origIdx % PALETTE.length,
      color,
    });
    angle += sweep;
  }

  return { segments };
}

/** 「split モード」シャッフル用: 各 split を順序通りに配置。
 *  splitOrder は元配列内インデックスの並び (重複あり、各項目が splitCount 回出現)。
 *  shuffle 時に各 split を独立にランダム配置するための entry point。
 */
export function buildLayoutFromSplitOrder(
  items: Item[],
  splitOrder: number[],
): WheelLayout {
  const weights = computeItemWeights(items);
  const total = weights.reduce((s, w) => s + w.weight, 0);
  if (total === 0) return { segments: [] };

  const weightPerSplit = new Map<number, number>();
  weights.forEach(({ idx, weight }) => {
    const N = Math.max(1, items[idx].splitCount);
    weightPerSplit.set(idx, weight / N);
  });

  let angle = 0;
  const segments: WheelSegment[] = [];
  for (const itemIdx of splitOrder) {
    const item = items[itemIdx];
    if (!item || !item.enabled) continue;
    const wPer = weightPerSplit.get(itemIdx) ?? 0;
    if (wPer <= 0) continue;
    const sweep = (wPer / total) * Math.PI * 2;
    const start = angle;
    const end = start + sweep;
    const color = item.customColor || PALETTE[itemIdx % PALETTE.length];
    segments.push({
      itemIndex: itemIdx,
      text: item.text,
      startAngle: start,
      endAngle: end,
      midAngle: (start + end) / 2,
      colorIndex: itemIdx % PALETTE.length,
      color,
    });
    angle += sweep;
  }
  return { segments };
}

/** ランダム配置の split order を生成。
 *
 *  maintainEvenness = false (デフォルト):
 *    各 split を完全独立にランダム配置 (Windows 同等)。
 *
 *  maintainEvenness = true:
 *    distributeSlots による標準均等配置をベースに、ランダム回転 + single 項目シャッフル。
 *    multi-split 項目の相対位置 (= 均等) は維持される。
 *    例: A(3),B,C,D → 標準 [A,B,A,C,A,D] → 回転1 [D,A,B,A,C,A] → singles 入替 [B,A,D,A,C,A]
 */
function shuffleArrayInPlaceLocal<T>(arr: T[]): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
}

export function generateRandomSplitOrder(
  items: Item[],
  maintainEvenness: boolean,
): number[] {
  if (!maintainEvenness) {
    // 完全独立ランダム
    const order: number[] = [];
    items.forEach((it, idx) => {
      if (!it.enabled) return;
      for (let k = 0; k < Math.max(1, it.splitCount); k++) {
        order.push(idx);
      }
    });
    shuffleArrayInPlaceLocal(order);
    return order;
  }

  // 均等維持モード: distributeSlots ベース
  const distrUnits = items
    .map((it, idx) => ({
      itemIndex: idx,
      N: Math.max(1, it.splitCount),
      enabled: it.enabled,
    }))
    .filter((u) => u.enabled);

  if (distrUnits.length === 0) return [];

  // 標準均等配置を取得
  const baseSlots = distributeSlots(
    distrUnits.map((u) => ({ itemIndex: u.itemIndex, N: u.N })),
  );
  const baseOrder = baseSlots.map((s) => s.itemIndex);
  const total = baseOrder.length;
  if (total === 0) return [];

  // ランダム回転 (multi の相対位置維持)
  const rotation = Math.floor(Math.random() * total);
  const rotated: number[] = new Array(total);
  for (let i = 0; i < total; i++) {
    rotated[(i + rotation) % total] = baseOrder[i];
  }

  // single 項目だけランダム入れ替え (multi 位置は固定)
  const multiSet = new Set(distrUnits.filter((u) => u.N > 1).map((u) => u.itemIndex));
  const singlePositions: number[] = [];
  const singleItems: number[] = [];
  for (let i = 0; i < total; i++) {
    if (!multiSet.has(rotated[i])) {
      singlePositions.push(i);
      singleItems.push(rotated[i]);
    }
  }
  shuffleArrayInPlaceLocal(singleItems);
  for (let i = 0; i < singlePositions.length; i++) {
    rotated[singlePositions[i]] = singleItems[i];
  }

  return rotated;
}

/** ShuffleOrder の有無 / モードを判定して適切な buildLayout を返すヘルパ */
export function buildLayoutForSet(
  items: Item[],
  setId: string,
  shuffleOrder: { setId: string; mode: 'item' | 'split'; order: number[] } | null,
): WheelLayout {
  const so = shuffleOrder && shuffleOrder.setId === setId ? shuffleOrder : null;
  if (so?.mode === 'split') {
    return buildLayoutFromSplitOrder(items, so.order);
  }
  if (so?.mode === 'item' && so.order.length === items.length) {
    const reordered = so.order.map((i) => items[i]);
    return buildLayout(reordered, so.order);
  }
  return buildLayout(items);
}

/** Items をシャッフルしてレイアウトを構築する（randomEachSpin 用）。 */
export function buildShuffledLayout(items: Item[]): WheelLayout {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return buildLayout(shuffled);
}

/** 累積確率分布を一様乱数 U ∈ [0,1) で逆引きして勝利セグメントを決める。
 *  シード保存方式: 同じ U を保持しておけば、確率変動後も同じ U で再計算可能。
 *  確率不変なら結果も不変、変動した場合は U が累積分布の境界をまたいだ時のみ結果が変化。
 */
export function pickWinnerWithSample(segments: WheelSegment[], U: number): number {
  if (segments.length === 0) return 0;
  const sizes = segments.map((s) => s.endAngle - s.startAngle);
  const total = sizes.reduce((a, b) => a + b, 0);
  if (total <= 0) return 0;
  let r = U * total;
  for (let i = 0; i < sizes.length; i++) {
    r -= sizes[i];
    if (r <= 0) return i;
  }
  return segments.length - 1;
}

/** 重み付きランダムで勝利セグメントインデックスを選ぶ (内部で乱数生成)。 */
export function pickWinnerIndex(segments: WheelSegment[]): number {
  return pickWinnerWithSample(segments, Math.random());
}

/** マーカー位置 → Canvas 座標系の角度 (0 = 3時方向) */
export function markerCanvasAngle(
  position: 'top' | 'right' | 'bottom' | 'left' | 'free',
  freeDeg?: number,
): number {
  switch (position) {
    case 'top':    return -Math.PI / 2;
    case 'right':  return 0;
    case 'bottom': return Math.PI / 2;
    case 'left':   return Math.PI;
    case 'free':   return ((freeDeg ?? 0) * Math.PI) / 180 - Math.PI / 2;
  }
}
